import { useState } from "react";
import { Download, FileSpreadsheet, AlertCircle, Loader2, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

const EXAMPLE_PHP = `a:3:{i:0;a:3:{s:4:"name";s:10:"John Smith";s:5:"email";s:15:"john@email.com";s:3:"age";i:30;}i:1;a:3:{s:4:"name";s:9:"Jane Doe";s:5:"email";s:15:"jane@email.com";s:3:"age";i:25;}i:2;a:3:{s:4:"name";s:12:"Bob Johnson";s:5:"email";s:14:"bob@email.com";s:3:"age";i:35;}}`;

export default function PhpToExcel() {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState("");
  const [unserializedData, setUnserializedData] = useState(null);

  const handleUnserialize = async () => {
    if (!input.trim()) {
      setError("Please paste PHP serialized data");
      return;
    }

    setLoading(true);
    setError("");
    setPreview("");

    try {
      const response = await axios.post(`${API}/php-unserialize`, { php_data: input });
      setUnserializedData(response.data.unserialized);
      setPreview(response.data.preview);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to unserialize PHP data");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!input.trim()) {
      setError("Please paste PHP serialized data");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${API}/php-to-excel`,
        { php_data: input },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'php_data.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to convert PHP data");
    } finally {
      setLoading(false);
    }
  };

  const loadExample = () => {
    setInput(EXAMPLE_PHP);
    setError("");
    setPreview("");
    setUnserializedData(null);
  };

  const copyPreview = () => {
    navigator.clipboard.writeText(preview);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-4">
            <FileSpreadsheet className="w-8 h-8 text-purple-600" />
          </div>
          <div className="inline-flex items-center gap-2 mb-3">
            <h1 className="text-4xl font-bold text-slate-900">
              PHP Unserialize to Excel
            </h1>
            <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full uppercase tracking-wider">
              One of a Kind
            </span>
          </div>
          <p className="text-lg text-slate-600">
            The only tool with recursive unserialization, JSON preview, and Excel export
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">
                  PHP Serialized Data
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadExample}
                  className="text-purple-600 hover:text-purple-700"
                >
                  Load Example
                </Button>
              </div>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder='Paste PHP serialized data... e.g., a:2:{s:4:"name";s:4:"John";s:3:"age";i:30;}'
                className="min-h-[400px] font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                Supports nested serialization, arrays, objects, and complex structures
              </p>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleUnserialize}
                  disabled={loading || !input.trim()}
                  className="flex-1 h-11 bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Unserialize & Preview
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleDownloadExcel}
                  disabled={loading || !input.trim()}
                  variant="outline"
                  className="flex-1 h-11 border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Excel
                </Button>
              </div>

              {/* Error */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Preview Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-slate-700">
                Unserialized Preview (JSON)
              </label>
              {preview && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyPreview}
                  className="text-purple-600 hover:text-purple-700"
                >
                  Copy
                </Button>
              )}
            </div>
            {preview ? (
              <pre className="bg-slate-50 rounded-lg p-4 text-sm font-mono overflow-auto max-h-[500px] border border-slate-200">
                {preview}
              </pre>
            ) : (
              <div className="bg-slate-50 rounded-lg p-8 text-center text-slate-400 min-h-[400px] flex items-center justify-center border border-slate-200">
                <div>
                  <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Click "Unserialize & Preview" to see the data</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="mt-8 bg-purple-50 border border-purple-200 rounded-xl p-6">
          <h3 className="font-semibold text-purple-900 mb-3">Features:</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-purple-800">
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Recursive unserialization (handles nested serialized strings)</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Preview unserialized data as formatted JSON</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Convert to Excel with row-wise data layout</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Flattens nested structures for better Excel columns</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Auto-sized columns and frozen headers</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Supports arrays, objects, and mixed data types</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
